import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { getPushCampaignFailureMessage } from '../../utils/pushCampaignValidation';
import {
  listPushCampaignRecipients,
  type PushCampaignRecipientPage,
  type PushRecipientStatus,
} from '../../services/resortAdminApi';

const PAGE_SIZE = 50;

const STATUS_FILTERS: Array<{ value: PushRecipientStatus | 'all'; label: string; }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'Sent', label: 'Sent' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Skipped', label: 'Skipped' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Claimed', label: 'In flight' },
];

const REASON_LABELS: Record<string, string> = {
  no_device: 'no device with a token',
  cancelled: 'campaign was stopped',
  account_deleted: 'account was deleted',
  not_active: 'account is disabled',
  opted_out: 'opted out of promo pushes',
  frequency_capped: 'reached the frequency cap',
  unregistered_token: 'every device token is dead',
  fcm_error: 'FCM rejected the message',
  max_attempts: 'retries were exhausted',
  lease_thrash: 'too many worker restarts',
};

interface RecipientsTableProps {
  campaignName: string;
  refreshToken: string | null;
}

export const RecipientsTable: React.FC<RecipientsTableProps> = props => {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<PushRecipientStatus | 'all'>('all');
  const [result, setResult] = useState<PushCampaignRecipientPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async (targetPage: number, targetStatus: PushRecipientStatus | 'all') => {
    setIsLoading(true);
    setError(null);
    try {
      setResult(await listPushCampaignRecipients(props.campaignName, targetStatus, targetPage, PAGE_SIZE));
    } catch (failure) {
      setError(getPushCampaignFailureMessage(failure, (failure as Error)?.message || 'Failed to load the recipients.'));
    } finally {
      setIsLoading(false);
    }
  }, [props.campaignName]);

  useEffect(() => {
    load(page, statusFilter);
  }, [load, page, statusFilter, props.refreshToken]);

  const totalPages = result == null ? 1 : Math.max(1, Math.ceil(result.pagination.total / result.pagination.page_size));

  const reasonOf = (skipReason: string | null, failReason: string | null): string => {
    const code = failReason || skipReason;
    if (!code) {
      return '-';
    }
    return REASON_LABELS[code] ?? code;
  };

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900">Recipients</h3>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={x => {
              setPage(1);
              setStatusFilter(x.target.value as PushRecipientStatus | 'all');
            }}
            className="rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-alps-primary focus:outline-none"
          >
            {STATUS_FILTERS.map(x => (
              <option key={x.value} value={x.value}>{x.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => load(page, statusFilter)}
            disabled={isLoading}
            className="inline-flex items-center rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {result != null && !!result.recipients_purged_at && (
        <p className="mt-3 text-sm text-gray-500">
          The recipient list was purged by the retention policy on {result.recipients_purged_at}. The
          campaign counters keep the final numbers.
        </p>
      )}

      {error != null && (
        <p className="mt-3 border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}

      {result != null && result.data.length === 0 && !result.recipients_purged_at && error == null && (
        <p className="mt-3 text-sm text-gray-500">
          {statusFilter === 'all' ? 'No recipients yet: the registry is filled at launch.' : 'No recipients with this status.'}
        </p>
      )}

      {result != null && result.data.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Devices</th>
                <th className="px-3 py-2">Attempts</th>
                <th className="px-3 py-2">Sent at</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {result.data.map(x => (
                <tr key={`${x.user_email}-${x.status}`}>
                  <td className="px-3 py-2 text-gray-900">{x.user_email}</td>
                  <td className="px-3 py-2">{x.status}</td>
                  <td className="px-3 py-2 text-gray-600">{reasonOf(x.skip_reason, x.fail_reason)}</td>
                  <td className="px-3 py-2 text-gray-600">
                    {x.devices_accepted ?? 0} of {x.devices_targeted ?? 0}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{x.attempts}</td>
                  <td className="px-3 py-2 text-gray-600">{x.sent_at ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result != null && result.pagination.total > result.pagination.page_size && (
        <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
          <span>
            Page {result.pagination.page} of {totalPages}, {result.pagination.total} recipients
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1 || isLoading}
              className="inline-flex items-center rounded-md border border-gray-300 px-2 py-1 hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages || isLoading}
              className="inline-flex items-center rounded-md border border-gray-300 px-2 py-1 hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
